import {
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { getAccessToken } from '@/lib/tokenStorage';

import {
  AddViewRequest,
  CustomView,
  SetDefaultViewRequest,
  UpdateViewRequest,
} from './types';

export type {
  AddViewRequest,
  CustomView,
  SetDefaultViewRequest,
  UpdateViewRequest,
} from './types';

function getBaseUrl(): string {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  return API_BASE_URL;
}

function getHeaders(): HeadersInit {
  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

async function fetchGet<T>(path: string, params?: URLSearchParams): Promise<T> {
  const base = `${getBaseUrl()}/api/v1/custom-view`;
  const url = path
    ? params
      ? `${base}/${path}?${params.toString()}`
      : `${base}/${path}`
    : params
      ? `${base}?${params.toString()}`
      : base;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as T;
}

async function fetchMutation<T>(
  path: string,
  method: 'POST' | 'PUT' | 'DELETE',
  body?: unknown,
): Promise<T> {
  const base = `${getBaseUrl()}/api/v1/custom-view`;
  const url = path ? `${base}/${path}` : base;

  const response = await fetch(url, {
    method,
    credentials: 'include',
    headers: getHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  if (
    response.status === 204 ||
    response.headers.get('content-length') === '0'
  ) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

async function fetchViewNames(params: {
  username: string;
  usage: string;
  app: string;
}): Promise<CustomView[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('username', params.username);
  searchParams.set('usage', params.usage);
  searchParams.set('app', params.app);
  return fetchGet<CustomView[]>('', searchParams);
}

async function fetchViewById(id: string): Promise<CustomView> {
  return fetchGet<CustomView>(id);
}

async function addViewFn(request: AddViewRequest): Promise<CustomView> {
  return fetchMutation<CustomView>('', 'POST', request);
}

async function updateViewFn(request: UpdateViewRequest): Promise<void> {
  return fetchMutation<void>(request.id, 'PUT', { view: request.view });
}

async function removeViewFn(id: string): Promise<void> {
  return fetchMutation<void>(id, 'DELETE');
}

async function setDefaultViewFn(request: SetDefaultViewRequest): Promise<void> {
  return fetchMutation<void>(`${request.id}/default`, 'PUT', {
    username: request.username,
    usage: request.usage,
  });
}

export const VIEW_NAMES_KEY = 'custom-views';

export const viewNamesOptions = (params: {
  username: string;
  usage: string;
  app: string;
}) =>
  queryOptions({
    queryKey: [VIEW_NAMES_KEY, 'view-names', params],
    async queryFn() {
      return fetchViewNames(params);
    },
  });

export const viewByIdOptions = (id: string) =>
  queryOptions({
    queryKey: [VIEW_NAMES_KEY, 'view-by-id', id],
    async queryFn() {
      return fetchViewById(id);
    },
  });

export function useAddViewRestMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: AddViewRequest) => addViewFn(request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [VIEW_NAMES_KEY] });
    },
  });
}

export function useUpdateViewRestMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: UpdateViewRequest) => updateViewFn(request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [VIEW_NAMES_KEY] });
    },
  });
}

export function useRemoveViewRestMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => removeViewFn(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [VIEW_NAMES_KEY] });
    },
  });
}

export function useSetDefaultViewRestMutation() {
  return useMutation({
    mutationFn: (request: SetDefaultViewRequest) => setDefaultViewFn(request),
  });
}
